import { useState, useEffect, useCallback } from 'react'
import { useFetch } from './useFetch'
import endpoints from '../_endpoints'

// Module-level cache with separate contexts per container
interface TokenCache {
  token: string | null
  expiry: number | null
}

const tokenCache: Record<string, TokenCache> = {}
// Track in-flight requests to prevent duplicate API calls
const pendingRequests: Record<string, Promise<void>> = {}
const DEFAULT_CONTAINER = 'desktopportal'

const getContainerKey = (containerName?: string) => containerName || DEFAULT_CONTAINER

const isTokenValid = (containerName?: string) => {
  const key = getContainerKey(containerName)
  const cache = tokenCache[key]

  if (!cache?.token || !cache?.expiry) return false
  // Consider expired if less than 5 minutes remaining
  return Date.now() < cache.expiry - 5 * 60 * 1000
}

const getCachedToken = (containerName?: string) => {
  const key = getContainerKey(containerName)
  return tokenCache[key]?.token || null
}

const setCachedToken = (containerName: string | undefined, token: string, expiry: number) => {
  const key = getContainerKey(containerName)
  tokenCache[key] = { token, expiry }
}

/**
 * Custom hook to manage SAS token with caching and expiry handling
 * Maintains separate token contexts for different containers
 * @param {string} containerName - Optional container name for token generation (defaults to 'desktopportal')
 * @returns {Object} Token utilities and state
 * @example
 * const { appendToken, hasToken } = useSasToken() // Uses 'desktopportal'
 * const secureUrl = appendToken(`${staticAssetsPath}/image.png`)
 * // With custom container (e.g., for support form)
 * const { appendToken } = useSasToken('userfiles')
 */
export const useSasToken = (containerName?: string) => {
  const [isLoading, fetchData] = useFetch()
  const [token, setToken] = useState<string | null>(getCachedToken(containerName))
  const [isExpired, setIsExpired] = useState(!isTokenValid(containerName))

  useEffect(() => {
    // Use cached token if valid
    if (isTokenValid(containerName)) {
      setToken(getCachedToken(containerName))
      setIsExpired(false)
      return
    }

    const key = getContainerKey(containerName)

    // If there's already a pending request for this container, wait for it
    if (key in pendingRequests) {
      pendingRequests[key].then(() => {
        setToken(getCachedToken(containerName))
        setIsExpired(!isTokenValid(containerName))
      })
      return
    }

    // Fetch new token if expired or missing
    const loadToken = async () => {
      try {
        const url = containerName
          ? `${endpoints.storage.getSasToken}?containerName=${containerName}`
          : endpoints.storage.getSasToken
        const res = await fetchData(url)
        if (res?.sasToken && res?.expiresOn) {
          const tokenValue = res.sasToken
          const expiryTime = new Date(res.expiresOn).getTime()
          setCachedToken(containerName, tokenValue, expiryTime)
          setToken(tokenValue)
          setIsExpired(false)
          return
        }
        throw new Error('SAS token unavailable in response')
      } catch (error) {
        console.error('Failed to fetch SAS token:', error)
        setToken(null)
        setIsExpired(true)
      }
    }

    // Store the promise to prevent duplicate requests
    pendingRequests[key] = loadToken().finally(() => {
      // Clean up the pending request once the whole retry chain has settled
      delete pendingRequests[key]
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerName])

  const appendToken = useCallback(
    (url: string) => {
      if (!token || isExpired || !url) return url
      // Remove any existing query parameters (old SAS tokens) before appending new token
      const baseUrl = url.split('?')[0]
      return `${baseUrl}?${token}`
    },
    [token, isExpired]
  )

  return {
    appendToken,
    hasToken: !!token && !isExpired,
    isLoading,
    isExpired
  }
}
